(() => {
  const FILTER_PENDING_KEY = 'av_filter_apply_pending';
  const FILTER_FOCUS_KEY = 'av_filter_result_focus';

  const rememberResultFocus = (root) => {
    try {
      sessionStorage.setItem(FILTER_FOCUS_KEY, JSON.stringify({ path: window.location.pathname, section: root.dataset.avSectionId, at: Date.now() }));
    } catch (_) {}
  };

  const restoreResultFocus = (root) => {
    try {
      const pending = JSON.parse(sessionStorage.getItem(FILTER_FOCUS_KEY) || 'null');
      if (!pending || pending.path !== window.location.pathname || pending.section !== root.dataset.avSectionId) return;
      sessionStorage.removeItem(FILTER_FOCUS_KEY);
      if (Date.now() - pending.at > 60000) return;
      const restore = () => {
        const active = document.activeElement;
        const hashTarget = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
        const activeIsResultAnchor = active === hashTarget && root.contains(hashTarget);
        if (active === document.body || active === document.documentElement || activeIsResultAnchor) {
          root.querySelector('[data-avc14-result-summary]')?.focus({ preventScroll: true });
        }
      };
      // Run after the existing hash-target focus, without stealing focus from a control.
      if (document.readyState === 'complete') requestAnimationFrame(restore);
      else window.addEventListener('pageshow', (event) => { if (!event.persisted) restore(); }, { once: true });
    } catch (_) {}
  };

  const emit = (eventName, root, payload = {}) => {
    if (!window.AVAnalytics || typeof window.AVAnalytics.legacy !== 'function') return false;
    return window.AVAnalytics.legacy(eventName, {
      source: 'collection',
      list_context:
        root?.dataset.collectionHandle ||
        root?.closest('[data-collection-handle]')?.dataset.collectionHandle ||
        window.location.pathname,
      ...payload
    });
  };

  const normalizeFacet = (facet) => String(facet || '').replace(/^filter\.p\.m\./, '');

  const rememberPendingFilters = (form) => {
    try {
      const controls = [...form.elements]
        .filter((control) => {
          if (!control.name || control.disabled || control.name === 'sort_by') return false;
          if (control.type === 'checkbox') return control.checked;
          return control.value?.trim() !== '';
        })
        .map((control) => ({ facet: normalizeFacet(control.name), value: control.value }));

      if (controls.length === 0) {
        sessionStorage.removeItem(FILTER_PENDING_KEY);
        return;
      }

      sessionStorage.setItem(FILTER_PENDING_KEY, JSON.stringify({
        path: new URL(form.action, window.location.href).pathname,
        created_at: Date.now(),
        controls
      }));
    } catch (_) {
      // Filtering remains a native GET flow when storage is unavailable.
    }
  };

  const emitPendingFilters = (root, attempt = 0) => {
    try {
      const pending = JSON.parse(sessionStorage.getItem(FILTER_PENDING_KEY) || 'null');
      if (!pending || pending.path !== window.location.pathname || Date.now() - pending.created_at > 30000) {
        sessionStorage.removeItem(FILTER_PENDING_KEY);
        return;
      }

      const privacy = window.Shopify?.customerPrivacy;
      const analyticsReady = typeof window.AVAnalytics?.legacy === 'function';
      const privacyReady = typeof privacy?.analyticsProcessingAllowed === 'function';
      const analyticsAllowed = privacyReady && privacy.analyticsProcessingAllowed() === true;
      if (!analyticsReady || !privacyReady || !analyticsAllowed) {
        if (attempt < 7) {
          window.setTimeout(() => emitPendingFilters(root, attempt + 1), Math.min(1000, 100 * (attempt + 1)));
        } else {
          sessionStorage.removeItem(FILTER_PENDING_KEY);
        }
        return;
      }

      const results = Number(root.querySelector('[data-avc14-result-count]')?.dataset.avc14ResultCount || 0);
      const remaining = pending.controls.filter(({ facet, value }) => {
        return !emit('av_filter_apply', root, { facet, value, results });
      });
      if (remaining.length === 0) {
        sessionStorage.removeItem(FILTER_PENDING_KEY);
      } else if (attempt < 7) {
        sessionStorage.setItem(FILTER_PENDING_KEY, JSON.stringify({ ...pending, controls: remaining }));
        window.setTimeout(() => emitPendingFilters(root, attempt + 1), Math.min(1000, 100 * (attempt + 1)));
      } else {
        sessionStorage.removeItem(FILTER_PENDING_KEY);
      }
    } catch (_) {
      // Analytics must never interrupt collection browsing.
    }
  };

  const syncGranolaPathState = (root) => {
    const granolaPaths = [...root.querySelectorAll('[data-avc14-granola-path]')];
    const granolaChapters = [...root.querySelectorAll('[data-avc14-granola-chapter]')];
    const granolaReset = root.querySelector('[data-avc14-granola-reset]');
    const sortForm = root.querySelector('[data-avc14-sort-form]');
    if (granolaPaths.length !== 3 || granolaChapters.length !== 3 || !granolaReset) return;

    const selectedChapter = granolaChapters.find((chapter) => `#${chapter.id}` === window.location.hash);
    const selectedPath = selectedChapter?.dataset.avc14GranolaChapter || '';
    const activeElement = document.activeElement;
    let activeElementWillHide = false;

    root.dataset.avc14GranolaMode = selectedPath || 'all';
    if (sortForm) {
      const baseAction = (sortForm.dataset.avc14BaseAction || sortForm.getAttribute('action') || '').split('#')[0];
      if (baseAction) {
        sortForm.dataset.avc14BaseAction = baseAction;
        sortForm.setAttribute('action', `${baseAction}#${selectedChapter ? selectedChapter.id : 'avc14-products'}`);
      }
    }
    granolaReset.toggleAttribute('aria-current', !selectedPath);
    if (!selectedPath) granolaReset.setAttribute('aria-current', 'page');

    granolaPaths.forEach((path) => {
      if (path.dataset.avc14GranolaPath === selectedPath) {
        path.setAttribute('aria-current', 'location');
      } else {
        path.removeAttribute('aria-current');
      }
    });

    granolaChapters.forEach((chapter) => {
      const isSelected = chapter === selectedChapter;
      const shouldHide = !root.hasAttribute('data-gp-anchor-only') && Boolean(selectedPath) && !isSelected;
      if (shouldHide && activeElement instanceof Element && chapter.contains(activeElement)) {
        activeElementWillHide = true;
      }
      chapter.hidden = shouldHide;
      if (isSelected) {
        chapter.dataset.avc14GranolaSelected = 'true';
      } else {
        delete chapter.dataset.avc14GranolaSelected;
      }
    });

    if (activeElementWillHide) {
      const activePath = granolaPaths.find((path) => path.dataset.avc14GranolaPath === selectedPath);
      activePath?.focus({ preventScroll: true });
    }
  };

  const initCollection = (root) => {
    if (!root || root.dataset.avc14Ready === 'true') return;
    root.dataset.avc14Ready = 'true';

    const sort = root.querySelector('[data-avc14-sort]');
    const sortForm = root.querySelector('[data-avc14-sort-form]');
    const filterForm = root.querySelector('[data-avc14-filter-form]');
    const filterDisclosure = root.querySelector('[data-avc14-filter-disclosure]');
    if (window.matchMedia('(min-width: 750px)').matches) {
      root.querySelectorAll('[data-avc14-chapter-intro]').forEach((intro) => { intro.open = true; });
    }

    sort?.addEventListener('change', () => {
      // Native sorting returns the flat grid, so its result target must exist
      // even when a chapter was selected in the preceding editorial view.
      if (sortForm) sortForm.setAttribute('action', `${(sortForm.dataset.avc14BaseAction || sortForm.getAttribute('action') || '').split('#')[0]}#avc14-products`);
      sortForm?.requestSubmit();
    });

    filterForm?.addEventListener('submit', () => {
      rememberResultFocus(root);
      rememberPendingFilters(filterForm);
      const selectedInputs = [...filterForm.querySelectorAll('input[type="checkbox"]:checked')];
      const selectedLabels = selectedInputs
        .map(
          (input) =>
            input.closest('label')?.querySelector('[data-avc14-filter-value-label]')?.textContent?.trim() ||
            input.value
        )
        .filter(Boolean);
      const selectedGroups = [...new Set(selectedInputs.map((input) => input.name).filter(Boolean))];
      emit('australia:collection_filter_applied', root, {
        filter_group: selectedGroups.join('|') || 'price_range',
        filter_value: selectedLabels.join('|') || 'price_or_none'
      });
    });

    sortForm?.addEventListener('submit', () => {
      rememberResultFocus(root);
      emit('australia:collection_filter_applied', root, {
        filter_group: 'sort_by',
        filter_value: sort?.value || 'default'
      });
    });

    filterDisclosure?.addEventListener('toggle', () => {
      if (filterDisclosure.open) emit('australia:collection_filter_opened', root);
    });
    filterDisclosure?.addEventListener('keydown', (event) => {
      if (!filterDisclosure.open) return;
      const summary = filterDisclosure.querySelector(':scope > summary');
      if (event.key === 'Escape') {
        event.preventDefault();
        filterDisclosure.open = false;
        summary?.focus({ preventScroll: true });
      }
      if (event.key === 'Tab') {
        const controls = [...filterDisclosure.querySelectorAll('summary, a[href], button, input, select')]
          .filter((control) => !control.disabled && control.getClientRects().length > 0);
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first?.focus();
        }
      }
    });

    root.querySelectorAll('.avc14-active-filters a').forEach((link) => {
      link.addEventListener('click', () => {
        rememberResultFocus(root);
        if (link.classList.contains('avc14-active-filters__clear')) {
          emit('australia:collection_filter_reset', root, { action: 'clear_all' });
        } else {
          emit('australia:collection_filter_removed', root, { filter_value: link.textContent.trim() });
        }
      });
    });

    root.querySelector('.avc14-pagination__more')?.addEventListener('click', () => {
      emit('australia:collection_page_next', root);
    });

    const granolaPathNav = root.querySelector('[data-avc14-granola-paths]');
    const granolaControls = [...(granolaPathNav?.querySelectorAll('a') || [])];
    if (granolaControls.length === 4) {
      granolaPathNav.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const currentControl = event.target.closest('a');
        const currentIndex = granolaControls.indexOf(currentControl);
        if (currentIndex < 0) return;

        let nextIndex = currentIndex;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (currentIndex + 1) % granolaControls.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (currentIndex - 1 + granolaControls.length) % granolaControls.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = granolaControls.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        granolaControls[nextIndex].focus();
        granolaControls[nextIndex].click();
      });
      syncGranolaPathState(root);
    }

    const serverEmpty = root.querySelector('.avc14-empty');
    if (serverEmpty && !serverEmpty.hidden) {
      emit('australia:collection_no_results', root, { result_count: 0 });
    }

    emitPendingFilters(root);
    restoreResultFocus(root);
  };

  window.addEventListener('hashchange', () => {
    document.querySelectorAll('[data-avc14-collection-root][data-avc14-granola-chapters-ready="true"]')
      .forEach(syncGranolaPathState);
  });

  const init = (context = document) => {
    context.querySelectorAll('[data-avc14-collection-root]').forEach(initCollection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
